import { memo, useEffect, useState } from 'react'
import { Radio, Undo2 } from 'lucide-react'
import type { ScheduleItem } from '../../types/index.js'
import { formatTime, scheduleItemTitle } from '../../lib/utils.js'
import { isWaitingLiveDue } from '../../lib/liveBreakState.js'
import { getServerNowMs } from '../../hooks/useServerClock.js'

/**
 * Bandeau du décrochage en cours : l'antenne est sur le comblage, le direct
 * attend de revenir.
 *
 * Il prend la place de `LiveControlBanner` — qui ne s'affiche que sur un direct
 * à l'antenne — pour que l'opérateur garde un seul endroit à regarder pendant
 * toute la durée du bloc, décrochage compris.
 *
 * « Reprendre » écourte le comblage et ramène le direct décroché : quand une
 * reprise est déjà posée en grille, elle est avancée ; sur un décrochage sans
 * durée fixe, elle est créée à cet instant, jusqu'à la fin du comblage.
 *
 * « Prendre » fait passer le direct SUIVANT de la grille (une autre émission,
 * souvent du même studio) dès que son heure est passée : il attendait la
 * coupure du direct précédent et ne part pas tout seul pendant le décrochage.
 * Le 28/09/2026, seul « Reprendre » était proposé alors que « LMFC Zone » aurait
 * dû être à l'antenne depuis 18:40:04.
 *
 * Avant son heure, « Prendre … maintenant » le fait passer en avance quand il
 * est la prochaine ancre (`canTakeEarly`) : le direct précédent a fini plus tôt,
 * l'infoservice tourne, l'émission suivante est prête. Geste à confirmer —
 * il avance une émission sur la grille —, les pubs prévues avant passent
 * d'abord et le direct garde sa fin de grille.
 */

interface LiveResumeBannerProps {
  /** Direct dont on a décroché. */
  brokenFrom: ScheduleItem
  /** Reprise déjà posée en grille, s'il y en a une. */
  resumeItem: ScheduleItem | null
  /** Premier direct d'un autre bloc encore en attente, s'il y en a un. */
  waitingLive?: ScheduleItem | null
  /** Le direct en attente est la prochaine ancre : il peut être pris en avance. */
  canTakeEarly?: boolean
  disabled:   boolean
  pending:    boolean
  onResume:   () => void
  onTake?:    (item: ScheduleItem, early: boolean) => void
  /** Horloge figée pour les stories et les tests. */
  now?:       number
}

export const LiveResumeBanner = memo(function LiveResumeBanner({
  brokenFrom, resumeItem, waitingLive = null, canTakeEarly = false, disabled, pending, onResume, onTake, now: fixedNow,
}: LiveResumeBannerProps) {
  const [tick, setTick] = useState(getServerNowMs)
  const [confirmingEarly, setConfirmingEarly] = useState(false)
  // « Prendre » apparaît à la seconde où l'heure de grille du direct suivant passe.
  useEffect(() => {
    if (fixedNow !== undefined || !waitingLive) return
    const timer = setInterval(() => setTick(getServerNowMs()), 1000)
    return () => clearInterval(timer)
  }, [fixedNow, waitingLive])
  const now = fixedNow ?? tick

  const brokenTitle = scheduleItemTitle(brokenFrom, 'Direct')
  const waitingTitle = waitingLive ? scheduleItemTitle(waitingLive, 'Direct suivant') : null
  const canTake = !!waitingLive && !!onTake && isWaitingLiveDue(waitingLive, now)
  const canTakeNowEarly = !canTake && !!waitingLive && !!onTake && canTakeEarly
    && new Date(waitingLive.startTime).getTime() > now
  const showConfirm = confirmingEarly && canTakeNowEarly

  return (
    <div className="monitor-manual-live" data-breaking="true">
      <div className="monitor-manual-live__copy">
        <strong>Décrochage en cours — {brokenTitle}</strong>
        <span>
          {resumeItem
            ? `Le direct revient tout seul à ${formatTime(resumeItem.startTime)}.`
            : 'Aucune reprise programmée : le direct revient quand vous raccrochez.'}
        </span>
        {waitingLive && (
          <span>
            {canTake
              ? `${waitingTitle} attend depuis ${formatTime(waitingLive.startTime)} : il ne partira pas tout seul.`
              : `Ensuite : ${waitingTitle} à ${formatTime(waitingLive.startTime)}.`}
          </span>
        )}
        {showConfirm && waitingLive && (
          <span role="alert">
            Prendre {waitingTitle} maintenant, au lieu de {formatTime(waitingLive.startTime)} ?
            {' '}Les pubs prévues avant passent d’abord ; il garde sa fin à {formatTime(waitingLive.endTime)}.
          </span>
        )}
      </div>

      {!showConfirm && (
        <button
          type="button"
          className="tp-button tp-button--critical monitor-manual-live__button"
          disabled={disabled || pending}
          onClick={onResume}
          title="Ramène le direct décroché à l'antenne maintenant et retire le reste du comblage"
        >
          {pending
            ? <><Undo2 size={16} aria-hidden="true" />Reprise…</>
            : <><Radio size={16} aria-hidden="true" />Reprendre {brokenTitle}</>}
        </button>
      )}

      {canTake && waitingLive && onTake && (
        <button
          type="button"
          className="tp-button tp-button--critical monitor-manual-live__button"
          disabled={disabled || pending}
          onClick={() => onTake(waitingLive, false)}
          title="Fait passer ce direct à l'antenne maintenant, jusqu'à sa fin de grille, et retire le reste du comblage"
        >
          <Radio size={16} aria-hidden="true" />Prendre {waitingTitle}
        </button>
      )}

      {canTakeNowEarly && waitingLive && onTake && (showConfirm ? (
        <>
          <button
            type="button"
            className="tp-button tp-button--critical monitor-manual-live__button"
            disabled={disabled || pending}
            onClick={() => { setConfirmingEarly(false); onTake(waitingLive, true) }}
          >
            <Radio size={16} aria-hidden="true" />Confirmer : prendre {waitingTitle}
          </button>
          <button
            type="button"
            className="tp-button tp-button--ghost monitor-manual-live__button"
            disabled={pending}
            onClick={() => setConfirmingEarly(false)}
          >
            Annuler
          </button>
        </>
      ) : (
        <button
          type="button"
          className="tp-button tp-button--outline-warning monitor-manual-live__button"
          disabled={disabled || pending}
          onClick={() => setConfirmingEarly(true)}
          title="Fait passer ce direct avant son heure de grille, après les pubs prévues ; il garde sa fin de grille"
        >
          <Radio size={16} aria-hidden="true" />Prendre {waitingTitle} maintenant
        </button>
      ))}
    </div>
  )
})
